import { AgenticFlowByline } from '@agentic-flow-studio/shared/components/agentic-flow-byline';
import { Alert, AlertDescription } from '@agentic-flow-studio/shared/ui/alert';
import { Button } from '@agentic-flow-studio/shared/ui/button';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle, MailCheck } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { PublicBrand } from '@/components/public-brand';
import { authClient } from '@/utils/betterAuth';

const forgotPasswordSchema = z.object({
  email: z.string().email('Invalid email address'),
});

type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>;

export default function ForgotPassword() {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit = async (data: ForgotPasswordFormValues) => {
    setError(null);
    setIsLoading(true);

    try {
      const { error: requestError } = await authClient.requestPasswordReset({
        email: data.email,
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (requestError) {
        setError(requestError.message || 'Failed to send reset email. Please try again.');
        return;
      }

      // Better Auth intentionally reports success even for an unknown email, to avoid
      // leaking which addresses have accounts — the UI mirrors that.
      setSent(true);
    } catch (err: unknown) {
      console.error('Forgot password error:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to send reset email. Please try again.';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="min-h-screen flex flex-col">
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md space-y-6">
          <PublicBrand />
          <div className="space-y-8 bg-card p-8 rounded-xl border border-border shadow-sm">
            {sent ? (
              <div className="text-center space-y-4">
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                  <MailCheck className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold tracking-tight">Check your email</h2>
                  <p className="text-sm text-muted-foreground mt-2">
                    If an account exists for that address, we've sent a link to reset your password.
                  </p>
                </div>
                <Button className="w-full mt-4" onClick={() => navigate('/login')}>
                  Back to login
                </Button>
              </div>
            ) : (
              <>
                <div className="text-center">
                  <h2 className="text-2xl font-bold tracking-tight">Reset your password</h2>
                  <p className="text-sm text-muted-foreground mt-2">
                    Enter your email and we'll send you a reset link
                  </p>
                </div>

                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
                  <div className="space-y-2">
                    <label htmlFor="email" className="text-sm font-medium">
                      Email
                    </label>
                    <input
                      id="email"
                      type="email"
                      placeholder="name@example.com"
                      {...register('email')}
                      className="w-full px-3 py-2 border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                    {errors.email && (
                      <p className="text-xs text-destructive">{errors.email.message}</p>
                    )}
                  </div>
                  <Button className="w-full mt-4" type="submit" disabled={isLoading}>
                    {isLoading ? 'Sending...' : 'Send reset link'}
                  </Button>
                </form>

                <div className="text-center text-sm">
                  <button
                    onClick={() => navigate('/login')}
                    className="font-medium text-primary hover:underline"
                  >
                    Back to login
                  </button>
                </div>
              </>
            )}
          </div>
          <p className="text-center">
            <AgenticFlowByline />
          </p>
        </div>
      </div>
    </main>
  );
}
